import { useState } from "react";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LanguageIcon from "@mui/icons-material/Language";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useTranslation } from "react-i18next";
import { changeLocale, SUPPORTED_LOCALES } from "../i18n";
import { profileApi } from "../api/endpoints";
import { useSession } from "../hooks/useSession";

const LABELS = { es: "ES", en: "EN", fr: "FR", it: "IT" };

/**
 * Selector de idioma. Si hay sesión abierta, la preferencia se guarda en el
 * perfil para que el portal y los correos usen el mismo idioma.
 */
export function LanguageMenu({ onDark = false, size = "small" }) {
  const { t, i18n } = useTranslation();
  const { status, updateUser } = useSession();
  const [anchor, setAnchor] = useState(null);

  const current = i18n.resolvedLanguage?.split("-")[0] ?? "es";

  const select = async (locale) => {
    setAnchor(null);
    if (locale === current) return;
    changeLocale(locale);

    if (status === "authenticated") {
      try {
        const data = await profileApi.update({ locale });
        updateUser(data.user);
      } catch {
        // La preferencia ya se aplicó en el navegador; si el guardado falla
        // no se interrumpe al usuario, se reintentará al próximo cambio.
      }
    }
  };

  return (
    <>
      <Button
        size={size}
        onClick={(event) => setAnchor(event.currentTarget)}
        startIcon={<LanguageIcon />}
        endIcon={<ExpandMoreIcon />}
        aria-label={t("nav.changeLanguage")}
        aria-haspopup="menu"
        sx={{
          color: onDark ? "#fff" : "text.secondary",
          borderColor: onDark ? "rgba(255,255,255,0.3)" : "divider",
        }}
        variant="outlined"
      >
        {LABELS[current] ?? current.toUpperCase()}
      </Button>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        {SUPPORTED_LOCALES.map((locale) => (
          <MenuItem
            key={locale}
            selected={locale === current}
            onClick={() => select(locale)}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {locale === current && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{t(`languages.${locale}`)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
